<script setup lang="ts">
import { computed } from 'vue'

type DistributionValue = number | string | null | undefined

const props = defineProps<{
  leftLabel: string
  leftValue: DistributionValue
  rightLabel: string
  rightValue: DistributionValue
  leftFormattedValue?: string
  rightFormattedValue?: string
  ariaLabel?: string
}>()

const distribution = computed(() => {
  const leftValue = toNumber(props.leftValue)
  const rightValue = toNumber(props.rightValue)
  const totalValue = leftValue + rightValue

  return {
    leftValue,
    rightValue,
    leftPercentage: percentage(leftValue, totalValue),
    rightPercentage: percentage(rightValue, totalValue),
  }
})

const computedAriaLabel = computed(() => {
  return (
    props.ariaLabel ??
    `${props.leftLabel}: ${distribution.value.leftPercentage}%, ${props.rightLabel}: ${distribution.value.rightPercentage}%`
  )
})

function toNumber(value: DistributionValue) {
  const numericValue = Number(value ?? 0)

  return Number.isFinite(numericValue) ? numericValue : 0
}

function percentage(value: number, total: number) {
  if (total <= 0) {
    return 0
  }

  return Math.round((value / total) * 100)
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-start justify-between gap-4">
      <div>
        <p class="text-surface-500 text-sm">{{ leftLabel }}</p>
        <p class="text-surface-950 font-semibold">
          {{ distribution.leftPercentage }}% · {{ leftFormattedValue ?? distribution.leftValue }}
        </p>
      </div>
      <div class="text-right">
        <p class="text-surface-500 text-sm">{{ rightLabel }}</p>
        <p class="text-surface-950 font-semibold">
          {{ distribution.rightPercentage }}% · {{ rightFormattedValue ?? distribution.rightValue }}
        </p>
      </div>
    </div>

    <div class="bg-surface-100 flex h-4 overflow-hidden rounded-full" role="img" :aria-label="computedAriaLabel">
      <div class="bg-action h-full" :style="{ width: `${distribution.leftPercentage}%` }" />
      <div class="bg-success-400 h-full" :style="{ width: `${distribution.rightPercentage}%` }" />
    </div>
  </div>
</template>
